import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import useLoginWithConnection from './useLoginWithConnection';
import { LocalStorageKeys } from '@/constants/localStorageKeys';
import { connectionHasEmbeddedMultiTenantLogin } from '@/pages/EmbeddedFirstLogin/connectionNameToHrisProvider';
import { withTimeout } from '@/helpers/withTimeout';

const REDIRECT_TIMEOUT_MS = 10000;

const clearStorage = () => {
  const keysToKeep: string[] = [
    LocalStorageKeys.SKIP_INTAKE_FORM,
    LocalStorageKeys.ELECTROLYTE_THEME,
    LocalStorageKeys.I18NEXT_LANGUAGE,
  ];

  Object.keys(localStorage).forEach(key => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  sessionStorage.clear();
};

/**
 * Custom hook built to handle login attempts from HCM platforms (TriNet, ADP, etc.)
 *
 * If an HCM user is logging in to a specific connection, we'll destroy the existing session to
 * prevent someone who has multiple HCM users from accidentally logging in to the wrong user account.
 * This can happen if they fail to log out of Electric and switch HCM accounts. If we don't destroy
 * the session, they'll be automatically logged in to a different user account than intended.
 *
 * IMPORTANT: Only clears storage if the user is NOT authenticated. After successful authentication,
 * even if the connection parameter remains in the URL, we should NOT clear storage again to prevent
 * infinite loops or unnecessary re-authentication.
 *
 * When embedded (iframe), we avoid passing the `connection` parameter to loginWithRedirect.
 * Auth0's session cookie (SameSite=None) is sent even in cross-origin iframes, so Auth0 can
 * use its existing session to issue tokens directly — without redirecting to the IdP (Okta/TriNet),
 * which would fail because the IdP's SameSite=Lax cookies aren't sent in iframe navigations.
 *
 * Exception: connections with embedded multi-tenant login bypass the cached-session path
 * even in iframes, because their custom claims carry an org identifier and a user can
 * belong to multiple orgs. Reusing Auth0's cached session would miss an org change set
 * by the 3rd-party platform. See connectionHasEmbeddedMultiTenantLogin.
 */
const useDestroyExistingSSOSession = () => {
  const [isDestroyingSession, setIsDestroyingSession] = useState(true);
  const [hasDestroyedSession, setHasDestroyedSession] = useState(false);
  const { loginWithConnection, connectionRef } = useLoginWithConnection();
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
  } = useAuth0();

  const isEmbedded = window.self !== window.top;

  useEffect(() => {
    const destroySession = async () => {
      clearStorage();
      setIsDestroyingSession(false);
      setHasDestroyedSession(true);
    };

    // Wait for Auth0 to finish loading before checking authentication state
    if (auth0Loading) {
      return;
    }
    // Only clear storage if:
    // 1. A connection parameter exists (indicating SSO flow)
    // 2. User is NOT authenticated (prevent destroying a valid session) or the connection has embedded multi-tenant login
    if (
      (connectionRef.current && !isAuthenticated) ||
      connectionHasEmbeddedMultiTenantLogin(connectionRef.current)
    ) {
      destroySession().then(() => {
        // Embedded: use loginWithRedirect WITHOUT connection parameter.
        // Auth0's session cookie (SameSite=None) is sent even in cross-origin iframes.
        // Without connection, Auth0 uses its existing session to issue tokens
        // directly — avoiding the redirect to the IdP (Okta/TriNet) which would
        // show a login page because IdP cookies aren't sent in iframe context.
        //
        // Exception: connections with embedded multi-tenant login always use
        // loginWithConnection to force a fresh login. Their custom claims carry
        // an org identifier, and a user can belong to multiple orgs — reusing
        // the cached session would miss an org change made by the 3rd-party platform.
        const redirectPromise =
          isEmbedded &&
          !connectionHasEmbeddedMultiTenantLogin(connectionRef.current)
            ? loginWithRedirect({
                appState: {
                  returnTo: `${window.location.pathname}${window.location.search}`,
                },
              })
            : loginWithConnection();

        // loginWithRedirect/loginWithConnection normally navigate the browser
        // away, so this never resolves on the happy path. If the redirect is
        // rejected or silently blocked (e.g. third-party cookies disabled), this
        // timeout stops the app from being stuck on the spinner forever.
        withTimeout(
          Promise.resolve(redirectPromise),
          REDIRECT_TIMEOUT_MS,
          'Timed out waiting for SSO redirect after destroying session'
        ).catch(error => {
          console.error(
            'Failed to redirect after destroying SSO session',
            error
          );
          window?.DD_RUM?.addError(error, {
            location: 'useDestroyExistingSSOSession',
          });
          setHasDestroyedSession(false);
        });
      });
    } else {
      setIsDestroyingSession(false);
    }
  }, [
    loginWithConnection,
    loginWithRedirect,
    connectionRef,
    isAuthenticated,
    auth0Loading,
    isEmbedded,
  ]);

  return {
    isDestroyingSession,
    hasDestroyedSession,
  };
};

export default useDestroyExistingSSOSession;
